// Onglets de la campagne : annonce et lien, visites, mesures, réponses types.
import { useState, type FormEvent } from "react";
import { del, patch, post, put, upload } from "@core/api";
import { date, dateTime, money } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import {
  Button,
  Card,
  Empty,
  ErrorBox,
  FileDrop,
  Input,
  KeyValues,
  Loading,
  Modal,
  Notice,
  Select,
  Table,
  Textarea,
  fieldError,
  useToast,
} from "@core/ui";
import { CampaignFields, CampaignHistory, deleteCampaign, saveCampaign, type Campaign, type CampaignForm } from "./campaigns";
import { AppStatusBadge, Kpi, bpLabel, hoursLabel, parisDateTime, parisLocalToIso, rentLine } from "./shared";
import { Link, useNavigate } from "react-router";

// ---------------------------------------------------------------------------
// Annonce et lien

function CopyLine({ label, value }: { label: string; value: string }) {
  const toast = useToast();
  return (
    <div className="location-copy">
      <span className="stat-label">{label}</span>
      <code className="location-link">{value}</code>
      <Button
        small
        variant="secondaire"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            toast("Lien copié.");
          } catch {
            toast("Copie impossible : sélectionnez le lien puis copiez-le.", "erreur");
          }
        }}
      >
        Copier
      </Button>
    </div>
  );
}

export function AnnonceTab({ campaign: c }: { campaign: Campaign }) {
  const org = useOrg();
  const toast = useToast();
  const nav = useNavigate();
  const invalidate = useInvalidate();
  const editable = org.can("operate") && !["attribuee", "close"].includes(c.status);
  const [form, setForm] = useState<CampaignForm>({
    title: c.title,
    description: c.description,
    lease_regime: c.lease_regime,
    rent_cents: c.rent_cents,
    charges_cents: c.charges_cents,
    charges_mode: c.charges_mode,
    deposit_cents: c.deposit_cents,
    available_on: c.available_on ?? "",
  });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const refresh = () => invalidate(org.api(`/campaigns`));

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await saveCampaign(org.api, c.id, form);
      toast("Conditions enregistrées. Le changement est tracé dans l’historique.");
      refresh();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function addPhotos(files: File[]) {
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("caption", "");
        await upload(org.api(`/campaigns/${c.id}/photos`), fd);
      }
      toast(files.length > 1 ? "Photos ajoutées." : "Photo ajoutée.");
      refresh();
    } catch (err: any) {
      toast(err.message, "erreur");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="grid-2 location-top">
      <div className="stack">
        <Card title="Lien de candidature">
          {c.public_url && c.short_url ? (
            <>
              <CopyLine label="Page de candidature" value={c.public_url} />
              <CopyLine label="Lien court, à coller dans vos annonces" value={c.short_url} />
              <p className="row">
                <a className="bv-btn bv-btn-secondaire bv-btn-petit" href={`/candidature/${c.public_slug}`} target="_blank" rel="noreferrer">
                  Voir la page publique
                </a>
              </p>
              {c.accepting ? (
                <Notice tone="succes">Le lien accepte les candidatures.</Notice>
              ) : (
                <Notice tone="attention">Le lien reste consultable mais n’accepte plus de nouvelle candidature ({c.status_label.toLowerCase()}).</Notice>
              )}
            </>
          ) : (
            <Notice>Le lien de candidature et le lien court sont créés à l’ouverture de la campagne.</Notice>
          )}
          <p className="small muted">
            La page publique montre la ville, les conditions, les photos et les pièces qui seront demandées ; jamais l’adresse exacte ni les dossiers reçus. Les visiteurs peuvent signaler une annonce.
          </p>
        </Card>
        <Card title="Photos de l’annonce">
          {c.photos?.length ? (
            <ul className="list-plain location-photos">
              {c.photos.map((p, i) => (
                <li key={p.id}>
                  <img src={p.url} alt={p.caption || `Photo ${i + 1} de l’annonce`} loading="lazy" />
                  {editable ? (
                    <div className="stack-sm">
                      <Input
                        label={`Légende de la photo ${i + 1}`}
                        defaultValue={p.caption}
                        onBlur={async (e) => {
                          if (e.target.value === p.caption) return;
                          try {
                            await patch(org.api(`/campaigns/${c.id}/photos/${p.id}`), { caption: e.target.value });
                            refresh();
                          } catch (err: any) {
                            toast(err.message, "erreur");
                          }
                        }}
                      />
                      <Button
                        small
                        variant="discret"
                        onClick={async () => {
                          try {
                            await del(org.api(`/campaigns/${c.id}/photos/${p.id}`));
                            toast("Photo retirée et effacée.");
                            refresh();
                          } catch (err: any) {
                            toast(err.message, "erreur");
                          }
                        }}
                      >
                        Retirer
                      </Button>
                    </div>
                  ) : (
                    <span className="small muted">{p.caption}</span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">Aucune photo. Des photos du séjour, de la cuisine et de la salle d’eau aident les candidats à se projeter.</p>
          )}
          {editable ? <FileDrop multiple accept="image/jpeg,image/png,image/webp" label="Ajouter des photos" help="JPEG, PNG ou WebP, 12 photos au maximum. Évitez les visages et les plaques d’immatriculation." busy={uploading} onFiles={addPhotos} /> : null}
        </Card>
        {c.other_campaigns?.length ? (
          <Card title="Autres campagnes de ce logement">
            <ul className="list-plain location-timeline">
              {c.other_campaigns.map((o) => (
                <li key={o.id}>
                  <Link to={org.to(`campagnes/${o.id}`)}>{o.title}</Link> <span className="muted small">· {o.status_label} · créée le {date(o.created_at)}</span>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}
      </div>
      <div className="stack">
        <Card title="Conditions de la location">
          {editable ? (
            <form className="stack" onSubmit={save}>
              <ErrorBox error={error} />
              <CampaignFields form={form} setForm={setForm} error={error} furnished={c.property_furnished} />
              <div className="form-actions">
                <Button type="submit" busy={busy}>
                  Enregistrer les conditions
                </Button>
                {c.status === "brouillon" && c.counts.total === 0 ? (
                  <Button
                    variant="danger"
                    onClick={async () => {
                      try {
                        await deleteCampaign(org.api, c.id);
                        toast("Brouillon supprimé.");
                        invalidate(org.api("/campaigns"));
                        nav(org.to("campagnes"));
                      } catch (err: any) {
                        toast(err.message, "erreur");
                      }
                    }}
                  >
                    Supprimer le brouillon
                  </Button>
                ) : null}
              </div>
            </form>
          ) : (
            <KeyValues
              items={[
                ["Titre", c.title],
                ["Disponible le", date(c.available_on)],
                ["Loyer", rentLine(c.rent_cents, c.charges_cents, c.charges_mode)],
                ["Dépôt de garantie", <span className="num">{money(c.deposit_cents)}</span>],
                ["Publiée le", date(c.published_at)],
              ]}
            />
          )}
        </Card>
        <Card title="Historique de la campagne">{c.events?.length ? <CampaignHistory events={c.events} /> : <p className="muted">Aucun événement.</p>}</Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Visites

type Booking = { id: string; application_id: string; household_label: string; application_status: string; status: string; attendance: string | null; cancelled_by: string | null; cancel_reason: string; reminded_at: string | null; created_at: string };
type Slot = { id: string; starts_at: string; ends_at: string; capacity: number; location: string; notes: string; status: string; cancel_reason: string; booked: number; label: string; bookings: Booking[] };

export function VisitsTab({ campaign: c }: { campaign: Campaign }) {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const q = useGet<{ items: Slot[] }>(org.api(`/campaigns/${c.id}/visit-slots`));
  const [form, setForm] = useState({ start: "", duration: "30", capacity: "3", location: "", notes: "" });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState<{ kind: "slot" | "booking"; id: string; label: string } | null>(null);
  const [reason, setReason] = useState("");
  const [slotEdit, setSlotEdit] = useState<{ id: string; label: string; capacity: string; location: string; notes: string; booked: number } | null>(null);
  const [slotError, setSlotError] = useState<unknown>(null);
  const refresh = () => invalidate(org.api(`/campaigns/${c.id}/visit-slots`), org.api(`/campaigns/${c.id}/applications`), org.api("/location"));

  async function saveSlot() {
    if (!slotEdit) return;
    setSlotError(null);
    try {
      await put(org.api(`/visit-slots/${slotEdit.id}`), { capacity: Number(slotEdit.capacity), location: slotEdit.location, notes: slotEdit.notes });
      toast("Créneau modifié.");
      setSlotEdit(null);
      refresh();
    } catch (err) {
      setSlotError(err);
    }
  }
  const canEdit = org.can("operate") && !["attribuee", "close"].includes(c.status);
  const now = Date.now();

  async function create(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const iso = parisLocalToIso(form.start);
    setBusy(true);
    try {
      await post(org.api(`/campaigns/${c.id}/visit-slots`), {
        starts_at: iso,
        duration_minutes: Number(form.duration),
        capacity: Number(form.capacity),
        location: form.location,
        notes: form.notes,
      });
      toast("Créneau ajouté. Les candidats peuvent le réserver depuis leur espace.");
      setForm({ ...form, start: "" });
      refresh();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function confirmCancel() {
    if (!cancelling) return;
    try {
      if (cancelling.kind === "slot") await post(org.api(`/visit-slots/${cancelling.id}/cancel`), { reason });
      else await post(org.api(`/visit-bookings/${cancelling.id}/cancel`), { reason });
      toast("Annulation enregistrée ; les candidats concernés sont prévenus.");
      setCancelling(null);
      setReason("");
      refresh();
    } catch (err: any) {
      toast(err.message, "erreur");
    }
  }

  return (
    <div className="stack location-top">
      {canEdit ? (
        <Card title="Proposer un créneau">
          <form className="stack" onSubmit={create}>
            <ErrorBox error={error} />
            <div className="form-row">
              <Input label="Début (heure de Paris)" type="datetime-local" required value={form.start} error={fieldError(error, "starts_at")} onChange={(e) => setForm({ ...form, start: e.target.value })} />
              <Select
                label="Durée"
                value={form.duration}
                error={fieldError(error, "duration_minutes")}
                options={["15", "20", "30", "45", "60", "90", "120"].map((d) => ({ value: d, label: `${d} minutes` }))}
                onChange={(e) => setForm({ ...form, duration: e.target.value })}
              />
              <Input label="Candidatures accueillies" type="number" min={1} max={50} value={form.capacity} error={fieldError(error, "capacity")} onChange={(e) => setForm({ ...form, capacity: e.target.value })} help="Une visite groupée accueille plusieurs dossiers." />
            </div>
            <Input label="Lieu de rendez-vous" value={form.location} error={fieldError(error, "location")} onChange={(e) => setForm({ ...form, location: e.target.value })} help="Communiqué seulement aux candidats qui réservent." />
            <Textarea label="Indications (facultatif)" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            <div className="form-actions">
              <Button type="submit" busy={busy}>
                Ajouter le créneau
              </Button>
            </div>
          </form>
        </Card>
      ) : null}
      <Card
        title="Créneaux"
        actions={
          org.can("operate") ? (
            <Button
              small
              variant="secondaire"
              onClick={async () => {
                try {
                  const r = await post<{ bookings: number; recipients: number }>(org.api(`/campaigns/${c.id}/visit-reminders`), {});
                  toast(r.bookings ? `${r.bookings} rappel(s) envoyé(s) pour les visites des prochaines 48 heures.` : "Aucune visite à rappeler dans les prochaines 48 heures.", "info");
                  refresh();
                } catch (err: any) {
                  toast(err.message, "erreur");
                }
              }}
            >
              Envoyer les rappels (48 h)
            </Button>
          ) : null
        }
      >
        <p className="small muted">Horaires en heure de Paris. Les rappels partent une seule fois par réservation. La présence se note après la visite ; elle n’est visible que dans cette campagne.</p>
        {q.isLoading ? <Loading /> : null}
        <ErrorBox error={q.error} />
        {q.data && !q.data.items.length ? <Empty title="Aucun créneau.">Proposez des créneaux : les candidats les réservent depuis leur espace.</Empty> : null}
        {q.data?.items.map((s) => {
          const past = new Date(s.starts_at).getTime() <= now;
          const active = s.bookings.filter((b) => b.status === "confirmee");
          return (
            <section key={s.id} className={`location-slot ${s.status === "annule" ? "location-muted" : ""}`} aria-label={`Créneau du ${s.label}`}>
              <div className="row-between">
                <div>
                  <strong>{parisDateTime(s.starts_at)}</strong>
                  <span className="block muted small">
                    {s.status === "annule" ? `Créneau annulé${s.cancel_reason ? ` : ${s.cancel_reason}` : ""}` : `${s.booked} / ${s.capacity} réservation${s.capacity > 1 ? "s" : ""}${s.booked >= s.capacity ? " · complet" : ""}`}
                    {s.location ? ` · ${s.location}` : ""}
                  </span>
                </div>
                {s.status === "ouvert" && !past && canEdit ? (
                  <div className="row">
                    <Button small variant="discret" onClick={() => setSlotEdit({ id: s.id, label: s.label, capacity: String(s.capacity), location: s.location, notes: s.notes, booked: s.booked })}>
                      Modifier
                    </Button>
                    <Button small variant="danger" onClick={() => setCancelling({ kind: "slot", id: s.id, label: s.label })}>
                      Annuler le créneau
                    </Button>
                  </div>
                ) : null}
              </div>
              {s.bookings.length ? (
                <Table caption={`Réservations du ${s.label}`}>
                  <thead>
                    <tr>
                      <th>Candidature</th>
                      <th>Réservation</th>
                      <th>Présence</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {s.bookings.map((b) => (
                      <tr key={b.id}>
                        <td>
                          <Link to={org.to(`candidatures/${b.application_id}`)}>{b.household_label || "Candidature"}</Link>
                          <span className="block">
                            <AppStatusBadge status={b.application_status} />
                          </span>
                        </td>
                        <td>
                          {b.status === "confirmee" ? "Confirmée" : `Annulée par ${b.cancelled_by === "candidat" ? "le candidat" : "le bailleur"}`}
                          {b.cancel_reason ? <span className="block muted small">{b.cancel_reason}</span> : null}
                          {b.reminded_at ? <span className="block muted small">Rappel envoyé le {dateTime(b.reminded_at)}</span> : null}
                        </td>
                        <td>
                          {b.status === "confirmee" && past && org.can("operate") ? (
                            <select
                              aria-label={`Présence de ${b.household_label}`}
                              value={b.attendance ?? ""}
                              onChange={async (e) => {
                                try {
                                  await post(org.api(`/visit-bookings/${b.id}/attendance`), { attendance: e.target.value || null });
                                  refresh();
                                } catch (err: any) {
                                  toast(err.message, "erreur");
                                }
                              }}
                            >
                              <option value="">Non renseignée</option>
                              <option value="venu">Venu</option>
                              <option value="absent">Absent</option>
                            </select>
                          ) : b.attendance ? (
                            b.attendance === "venu" ? "Venu" : "Absent"
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                        <td className="right">
                          {b.status === "confirmee" && !past && canEdit ? (
                            <Button small variant="discret" onClick={() => setCancelling({ kind: "booking", id: b.id, label: b.household_label })}>
                              Annuler
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="small muted">Aucune réservation.</p>
              )}
              {active.length && s.notes ? <p className="small muted">Indications : {s.notes}</p> : null}
            </section>
          );
        })}
      </Card>
      <Modal
        open={!!slotEdit}
        onClose={() => setSlotEdit(null)}
        title="Modifier le créneau"
        footer={
          <>
            <Button variant="discret" onClick={() => setSlotEdit(null)}>
              Annuler
            </Button>
            <Button onClick={saveSlot}>Enregistrer</Button>
          </>
        }
      >
        {slotEdit ? (
          <div className="stack">
            <p className="small">{parisDateTime(q.data?.items.find((x) => x.id === slotEdit.id)?.starts_at)} · {slotEdit.booked} réservation(s) en cours.</p>
            <ErrorBox error={slotError} />
            <Input label="Candidatures accueillies" type="number" min={Math.max(1, slotEdit.booked)} max={50} value={slotEdit.capacity} error={fieldError(slotError, "capacity")} onChange={(e) => setSlotEdit({ ...slotEdit, capacity: e.target.value })} />
            <Input label="Lieu de rendez-vous" value={slotEdit.location} error={fieldError(slotError, "location")} onChange={(e) => setSlotEdit({ ...slotEdit, location: e.target.value })} />
            <Textarea label="Indications" rows={2} value={slotEdit.notes} onChange={(e) => setSlotEdit({ ...slotEdit, notes: e.target.value })} />
          </div>
        ) : null}
      </Modal>
      <Modal
        open={!!cancelling}
        onClose={() => setCancelling(null)}
        title={cancelling?.kind === "slot" ? "Annuler le créneau" : "Annuler la réservation"}
        footer={
          <>
            <Button variant="discret" onClick={() => setCancelling(null)}>
              Revenir
            </Button>
            <Button variant="danger" onClick={confirmCancel}>
              Confirmer l’annulation
            </Button>
          </>
        }
      >
        <div className="stack">
          <p>{cancelling?.kind === "slot" ? "Toutes les réservations de ce créneau sont annulées. Chaque candidat reçoit un message et peut choisir un autre créneau." : `La réservation de ${cancelling?.label} est annulée ; le candidat est prévenu.`}</p>
          <Textarea label="Motif communiqué aux candidats (facultatif)" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mesures

type Metrics = {
  views: { total: number; short_link: number; page: number; by_day: { day: string; count: number }[] };
  applications: { started: number; submitted: number; drafts: number; abandoned_before_submission: number; withdrawn: number; complete: number; complete_evaluated: number; retained: number; refused: number; waiting_list: number; to_complete: number; in_review: number };
  rates_bp: { submitted_over_started: number | null; started_over_views: number | null; complete_over_evaluated: number | null };
  delays: { first_submission_hours: number | null; median_decision_hours: number | null; decisions_measured: number; publication_to_attribution_days: number | null };
  visits: { slots: number; slots_cancelled: number; capacity: number; booked: number; cancelled_by_candidate: number; cancelled_by_landlord: number; attended: number; absent: number; not_noted: number };
  sources: { source: string; count: number }[];
  definitions: { label: string; text: string }[];
};

const SOURCES: Record<string, string> = { lien: "Lien de candidature", dossierfacile: "DossierFacile", manuel: "Saisie manuelle", import: "Import" };

export function MetricsTab({ campaign: c }: { campaign: Campaign }) {
  const org = useOrg();
  const q = useGet<Metrics>(org.api(`/campaigns/${c.id}/metrics`));
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} />;
  const m = q.data;
  const max = Math.max(1, ...m.views.by_day.map((d) => d.count));
  return (
    <div className="stack location-top">
      <Notice>Mesures agrégées sur cette campagne pour suivre le parcours de location. Elles ne portent jamais sur une personne et ne classent pas les dossiers.</Notice>
      <Card title="Parcours">
        <div className="stats">
          <Kpi label="Vues de la page" value={m.views.total} hint={`dont ${m.views.short_link} par le lien court`} />
          <Kpi label="Candidatures commencées" value={m.applications.started} hint={`${bpLabel(m.rates_bp.started_over_views)} des vues`} />
          <Kpi label="Candidatures déposées" value={m.applications.submitted} hint={`${bpLabel(m.rates_bp.submitted_over_started)} des candidatures commencées`} />
          <Kpi label="Dossiers complets" value={`${m.applications.complete} / ${m.applications.complete_evaluated}`} hint="parmi les dossiers dont la complétude a été contrôlée" />
          <Kpi label="Retenues" value={m.applications.retained} hint={`${m.applications.refused} non retenue(s), ${m.applications.waiting_list} en liste d’attente`} />
          <Kpi label="Abandons avant dépôt" value={m.applications.abandoned_before_submission} hint={`${m.applications.drafts} brouillon(s) en cours`} />
        </div>
      </Card>
      <div className="grid-2">
        <Card title="Délais">
          <KeyValues
            items={[
              ["Première candidature après publication", hoursLabel(m.delays.first_submission_hours)],
              ["Délai médian de décision", m.delays.decisions_measured ? `${hoursLabel(m.delays.median_decision_hours)} (${m.delays.decisions_measured} décision${m.delays.decisions_measured > 1 ? "s" : ""})` : "—"],
              ["De la publication à l’attribution", m.delays.publication_to_attribution_days === null ? "—" : `${m.delays.publication_to_attribution_days.toLocaleString("fr-FR")} jours`],
              ["Dossiers à compléter / en examen", `${m.applications.to_complete} / ${m.applications.in_review}`],
            ]}
          />
        </Card>
        <Card title="Visites">
          <KeyValues
            items={[
              ["Créneaux proposés", `${m.visits.slots} (${m.visits.capacity} places)`],
              ["Réservations en cours", m.visits.booked],
              ["Annulées par un candidat / par vous", `${m.visits.cancelled_by_candidate} / ${m.visits.cancelled_by_landlord}`],
              ["Venus / absents / non renseignés", `${m.visits.attended} / ${m.visits.absent} / ${m.visits.not_noted}`],
            ]}
          />
        </Card>
      </div>
      <Card title="Vues par jour">
        {m.views.by_day.length ? (
          <Table caption="Vues de la page publique par jour">
            <thead>
              <tr>
                <th>Jour</th>
                <th className="right">Vues</th>
                <th aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {m.views.by_day.map((d) => (
                <tr key={d.day}>
                  <td>{date(d.day)}</td>
                  <td className="right num">{d.count}</td>
                  <td aria-hidden="true" className="location-bar-cell">
                    <span className="location-bar" style={{ width: `${Math.round((d.count / max) * 100)}%` }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p className="muted">Aucune vue enregistrée.</p>
        )}
        {m.sources.length ? <p className="small muted">Origine des candidatures : {m.sources.map((s) => `${SOURCES[s.source] ?? s.source} (${s.count})`).join(", ")}.</p> : null}
      </Card>
      <Card title="Définitions">
        <dl className="location-defs">
          {m.definitions.map((d) => (
            <div key={d.label}>
              <dt>{d.label}</dt>
              <dd>{d.text}</dd>
            </div>
          ))}
        </dl>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Réponses types

export type Template = { id: string; title: string; body: string; updated_at: string };
export type TemplatesData = { items: Template[]; suggestions: { title: string; body: string }[]; variables: { name: string; label: string }[] };

export function TemplatesTab() {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const q = useGet<TemplatesData>(org.api("/reply-templates"));
  const [editing, setEditing] = useState<{ id?: string; title: string; body: string } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const refresh = () => invalidate(org.api("/reply-templates"));

  async function save() {
    if (!editing) return;
    setError(null);
    try {
      if (editing.id) await put(org.api(`/reply-templates/${editing.id}`), { title: editing.title, body: editing.body });
      else await post(org.api("/reply-templates"), { title: editing.title, body: editing.body });
      toast("Réponse type enregistrée.");
      setEditing(null);
      refresh();
    } catch (err) {
      setError(err);
    }
  }

  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} />;
  const existing = new Set(q.data.items.map((t) => t.title));
  return (
    <div className="grid-2 location-top">
      <Card title="Réponses types de l’espace" actions={org.can("operate") ? <Button small onClick={() => setEditing({ title: "", body: "" })}>Nouvelle réponse type</Button> : null}>
        <p className="small muted">Utilisables depuis les messages d’une candidature ou pour un envoi groupé. Le texte est toujours relu et modifiable avant l’envoi.</p>
        {q.data.items.length ? (
          <ul className="list-plain location-templates">
            {q.data.items.map((t) => (
              <li key={t.id}>
                <div className="row-between">
                  <strong>{t.title}</strong>
                  {org.can("operate") ? (
                    <span className="row">
                      <Button small variant="discret" onClick={() => setEditing({ id: t.id, title: t.title, body: t.body })}>
                        Modifier
                      </Button>
                      <Button
                        small
                        variant="discret"
                        onClick={async () => {
                          try {
                            await del(org.api(`/reply-templates/${t.id}`));
                            refresh();
                          } catch (err: any) {
                            toast(err.message, "erreur");
                          }
                        }}
                      >
                        Supprimer
                      </Button>
                    </span>
                  ) : null}
                </div>
                <pre className="message-body">{t.body}</pre>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">Aucune réponse type. Ajoutez un modèle proposé ou rédigez le vôtre.</p>
        )}
      </Card>
      <div className="stack">
        <Card title="Modèles proposés">
          <ul className="list-plain location-templates">
            {q.data.suggestions.map((s) => (
              <li key={s.title}>
                <div className="row-between">
                  <strong>{s.title}</strong>
                  {org.can("operate") && !existing.has(s.title) ? (
                    <Button small variant="secondaire" onClick={() => setEditing({ title: s.title, body: s.body })}>
                      Adapter et ajouter
                    </Button>
                  ) : existing.has(s.title) ? (
                    <span className="small muted">Déjà ajouté</span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </Card>
        <Card title="Variables disponibles">
          <ul className="list-plain small">
            {q.data.variables.map((v) => (
              <li key={v.name}>
                <code>{`{{${v.name}}}`}</code> — {v.label}
              </li>
            ))}
          </ul>
        </Card>
      </div>
      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Modifier la réponse type" : "Nouvelle réponse type"}
        footer={
          <>
            <Button variant="discret" onClick={() => setEditing(null)}>
              Annuler
            </Button>
            <Button onClick={save}>Enregistrer</Button>
          </>
        }
      >
        {editing ? (
          <div className="stack">
            <ErrorBox error={error} />
            <Input label="Titre" value={editing.title} error={fieldError(error, "title")} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
            <Textarea label="Texte" rows={10} value={editing.body} error={fieldError(error, "body")} onChange={(e) => setEditing({ ...editing, body: e.target.value })} help="Restez factuel et courtois : pas de formulation accusatoire. Variables entre doubles accolades." />
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
